import React from 'react';
import { FormValuesType } from 'src/UI/dynamicUI/types';
import {
  FormAccordionItem,
  FormAccordionItemContainer,
  ReadOnlyWrapper,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Box, Text, Button } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { FormTable } from 'src/common/components/form-table/FormTable';
import { FeedInfo } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import { IconPlus } from '@tabler/icons-react';
import { FundCodeDropdown } from './FundCodeDropdown';

type FundHoldingFormAccordionItemListProps = {
  isEditing: boolean;
  form: UseFormReturnType<FormValuesType>;
  productType: string;
  titleIcon?: React.ReactNode;
  feedInfo?: FeedInfo;
};

export const FUND_HOLDING = 'FundHolding';

export const FundHoldingFormAccordionItemList = ({
  form,
  isEditing,
  productType,
  titleIcon,
  feedInfo,
}: FundHoldingFormAccordionItemListProps) => {
  const untypedItems = form.getValues()[FUND_HOLDING];
  const fundHoldings = Array.isArray(untypedItems) ? untypedItems : [];
  const formValues = form.getValues();
  const productEntity = formValues[productType] as Record<string, unknown>;
  const providerReferenceNumber = productEntity?.provider_reference_number;
  const hasReferenceNumber =
    typeof providerReferenceNumber === 'string' && providerReferenceNumber.trim() !== '';
  const hasNoItems = fundHoldings.length === 0;

  const isConnectedToFeed = feedInfo?.isUpdatedAutomatically ?? false;
  const canEdit = isEditing && !isConnectedToFeed;

  const handleAddFundHolding = () => {
    form.insertListItem(FUND_HOLDING, { id: null, type: null, amount: null, fund_id: null });
  };

  const pricePerUnitTooltip =
    'Price per unit is filled automatically when the asset is saved.';
  const descriptionTooltip =
    'Description is filled automatically from the selected fund.';

  if (hasNoItems)
    return (
      <FundHoldingFormAccordionItemListNoItems
        form={form}
        productType={productType}
        titleIcon={titleIcon}
        canEdit={canEdit}
        onAddFundHolding={handleAddFundHolding}
      />
    );

  const tableContent = (
    <FormTable
      columns={[
        {
          accessor: 'fund.name',
          title: 'Description',
          disabled: true,
          tooltip: canEdit ? descriptionTooltip : undefined,
        },
        {
          accessor: 'fund.code',
          title: 'Fund code',
          customRender: (index: number) => (
            <FundCodeDropdown
              form={form}
              formFieldName={`${FUND_HOLDING}.${index}.fund_id`}
              fieldName="fund_id"
              disabled={!canEdit}
              editMode={canEdit}
            />
          ),
        },
        {
          accessor: 'fund.price_per_unit',
          title: 'Price per unit',
          disabled: true,
          tooltip: canEdit ? pricePerUnitTooltip : undefined,
        },
        {
          accessor: 'value_date',
          title: 'Valuation date',
        },
        {
          accessor: 'units_held',
          title: 'Units held',
        },
        {
          accessor: 'holding_value',
          title: 'Value',
        },
      ]}
      dataKey={FUND_HOLDING}
      form={form}
      showDeleteButton={canEdit}
    />
  );

  return (
    <FormAccordionItem value="fund_holdings" label="Fund holdings" childrenWrapper={null} titleIcon={titleIcon}>
      <>
        <FormAccordionItemContainer>
          {isConnectedToFeed ? (
            <ReadOnlyWrapper>{tableContent}</ReadOnlyWrapper>
          ) : (
            tableContent
          )}
        </FormAccordionItemContainer>
        {canEdit && (
          <Button
            mt="md"
            variant="secondary"
            onClick={handleAddFundHolding}
            leftSection={<IconPlus size={16} />}
            data-testid="add-fund-holding-button"
          >
            Add fund holding
          </Button>
        )}
        {hasReferenceNumber && isConnectedToFeed && (
          <Text mt="md">{`Populated from feed (Ref #${providerReferenceNumber})`}</Text>
        )}
      </>
    </FormAccordionItem>
  );
};

const FundHoldingFormAccordionItemListNoItems = ({
  form,
  productType,
  titleIcon,
  canEdit,
  onAddFundHolding,
}: {
  form: UseFormReturnType<FormValuesType>;
  productType: string;
  titleIcon?: React.ReactNode;
  canEdit?: boolean;
  onAddFundHolding?: () => void;
}) => {
  const formValues = form.getValues();
  const productEntity = formValues[productType] as Record<string, unknown>;
  const providerReferenceNumber = productEntity?.provider_reference_number;
  const hasReferenceNumber =
    typeof providerReferenceNumber === 'string' && providerReferenceNumber.trim() !== '';
  const getMessage = () => {
    if (hasReferenceNumber) {
      return `No funds are linked to the entered provider reference number (${providerReferenceNumber}). Please check if the number has been entered correctly.${canEdit ? ' You can also add fund holdings manually below.' : ''}`;
    }
    return canEdit
      ? 'Enter the provider reference number (in summary above) to link with the provider and allow an automatic feed of underlying fund holdings here, or add fund holdings manually below.'
      : 'Enter the provider reference number (in summary above) to link with the provider and allow an automatic feed of underlying fund holdings here.';
  };

  return (
    <FormAccordionItem value="fund_holdings" label="Fund holdings" childrenWrapper={null} titleIcon={titleIcon}>
      <FormAccordionItemContainer>
        <Box w="35%" mx="auto">
          <Text ta="center" c="dimmed">
            {getMessage()}
          </Text>
        </Box>
      </FormAccordionItemContainer>
      {canEdit && onAddFundHolding && (
        <Button
          mt="md"
          variant="secondary"
          onClick={onAddFundHolding}
          leftSection={<IconPlus size={16} />}
          data-testid="add-fund-holding-button"
        >
          Add fund holding
        </Button>
      )}
    </FormAccordionItem>
  );
};
